import React, { useState } from "react";
import { CheckCircle2, Clock, Bell, Video, KanbanSquare, ListChecks, FileText, Users, Workflow, Calendar as CalendarIcon, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const TIER1 = [
  { name: "Slack", desc: "Mute channels during your priority blocks." },
  { name: "Google Workspace", desc: "Calendar-aware focus sessions." },
  { name: "Microsoft Teams", desc: "Status sync + DND while focused." },
  { name: "Stripe", desc: "Billing & subscription management." },
  { name: "Intercom", desc: "In-app help and live chat." },
];

const PHASE2 = [
  { name: "Zoom", icon: Video, desc: "Auto-detect calls and pause widget refocus prompts." },
  { name: "Jira", icon: KanbanSquare, desc: "Pull active tickets into your priority hierarchy." },
  { name: "Asana", icon: ListChecks, desc: "Surface 'My Tasks' as priority work." },
  { name: "Notion", icon: FileText, desc: "Treat current Notion docs as deep-work sessions." },
  { name: "HubSpot", icon: Users, desc: "Score sales-rep focus during outreach blocks." },
  { name: "Salesforce", icon: Users, desc: "Aggregate focus scores per pipeline stage." },
  { name: "Zapier", icon: Workflow, desc: "Trigger any tool from FACE events." },
  { name: "Monday.com", icon: CalendarIcon, desc: "Sync boards into the priority hierarchy." },
];

export default function AppIntegrations() {
  const [notified, setNotified] = useState({});

  const notify = async (name) => {
    try {
      await api.post("/support/ticket", {
        category: "integration_request",
        subject: `Notify me when ${name} ships`,
        message: `User asked to be notified when ${name} integration ships.`,
      });
    } catch (_) { /* fire-and-forget */ }
    setNotified((s) => ({ ...s, [name]: true }));
    toast.success(`We'll email you when ${name} ships.`);
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="app-integrations-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">Integrations</p>
        <h1 className="font-display text-3xl text-[#1A1A2E] mt-1">Connect the tools you use.</h1>
        <p className="text-sm text-[#555568] mt-2">
          Phase 1 integrations are live. Phase 2 ships in the next two quarters - tap "Notify me" to be first in line.
        </p>
      </div>

      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-4">Active</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {TIER1.map((t) => (
            <div key={t.name} className="rounded-md border border-[#E8ECF2] bg-[#F8F9FC] p-4" data-testid={`integration-active-${t.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              <CheckCircle2 className="text-[#1D9E75]" size={16} />
              <p className="font-display font-bold text-[#1A1A2E] mt-2">{t.name}</p>
              <p className="text-xs text-[#555568] mt-1 leading-relaxed">{t.desc}</p>
              <button className="text-xs text-[#2C6BAC] mt-3 hover:underline">Manage</button>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6" data-testid="integrations-phase2">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-xl font-bold text-[#1A1A2E]">Coming soon · Phase 2</h2>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#BA7517] font-semibold">8 integrations · Q3-Q4 2026</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {PHASE2.map(({ name, icon: Icon, desc }) => (
            <div
              key={name}
              className="rounded-md border border-[#E8ECF2] bg-[#F8F9FC] p-4 flex flex-col"
              data-testid={`integration-phase2-${name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            >
              <div className="flex items-center justify-between">
                <Icon className="text-[#1A3A5C]" size={16} />
                <Clock className="text-[#BA7517]" size={14} />
              </div>
              <p className="font-display font-bold text-[#1A1A2E] mt-2">{name}</p>
              <p className="text-xs text-[#555568] mt-1 leading-relaxed flex-1">{desc}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => notify(name)}
                disabled={!!notified[name]}
                className="mt-3 text-xs"
                data-testid={`integration-notify-${name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              >
                <Bell size={12} className="mr-1.5" />
                {notified[name] ? "We'll email you" : "Notify me"}
              </Button>
            </div>
          ))}
        </div>
        <div className="mt-5 rounded-md bg-[#1A3A5C] text-white p-4 flex items-start gap-3">
          <Zap size={16} className="mt-0.5" />
          <div>
            <p className="text-sm font-semibold">Have a tool you can't live without?</p>
            <p className="text-xs text-[#E8ECF2]/85 mt-1">Tell us - Compass + Command tier customers shape our integration roadmap.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
